import clsx from 'clsx';
import { IAnyType } from 'common/types/common';
import {
  Article,
  ComponentCustomCta,
  ComponentCustomPostRepeatableSection,
} from 'common/types/content/graphql/graphql';
import { normalizeLocalizedHref } from 'common/utils/url';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';

import { ContentHeading } from '../ContentHeading';
import { ContentRichText } from '../ContentRichText';

const CTA = dynamic(() => import('common/components/Cta').then(mod => mod.CTA));

export const ContentArticleContent = ({
  classNameSection,
  article,
}: {
  classNameSection?: string;
  article: Article;
}) => {
  const { i18n } = useTranslation();

  const renderQuote = (quote: ComponentCustomPostRepeatableSection[], sectionId: string) => {
    if (!quote.length) {
      return <></>;
    }
    return (
      <div className="work-heading mb-16 mt-20 max-w-full border border-[#080711] bg-white/5  px-4 py-8 lg:px-8 lg:py-12 text-center drop-shadow-xl md:px-8 lg:px-12">
        {quote.map((item: IAnyType, index: number) => {
          if (item.type === 'paragraph') {
            return (
              <p key={`content-${sectionId}-${index.toString()}`} className="mt-2">
                {item.children.map((child: IAnyType) => {
                  const childKey =
                    child.id ||
                    `content-quote-child-${sectionId}-${index.toString()}-${
                      child.url || child.text || child.type
                    }`;
                  if (child.type === 'link') {
                    const normalizedHref = normalizeLocalizedHref(child.url, i18n.language);

                    if (
                      (child.url.includes('http') || child.url.includes('www')) &&
                      !child.url.includes('nordit.co')
                    ) {
                      return (
                        <a
                          key={childKey}
                          className="text-primary"
                          href={normalizedHref}
                          rel={child.rel || 'noopener noreferrer'}
                          target="_blank">
                          {child.text || child.children?.[0]?.text}
                        </a>
                      );
                    }
                    return (
                      <Link
                        key={`content-quote-link-${sectionId}-${index.toString()}-${child.url}`}
                        className="text-primary"
                        href={normalizedHref}>
                        {child.text || child.children?.[0]?.text}
                      </Link>
                    );
                  }
                  return <span key={childKey}>{child.text}</span>;
                })}
              </p>
            );
          }
          return null;
        })}
      </div>
    );
  };

  return (
    <section className="container border-b-[1px] border-b-gray-200/10">
      <div className="mx-auto max-w-full px-5 pb-20 pt-32 text-xl leading-[1.8rem] sm:max-w-[560px] md:max-w-[680px] lg:max-w-[900px] lg:pb-32 lg:pt-40 lg:text-2xl lg:leading-[2.2rem] xl:max-w-[1060px]">
        {article?.content?.map((item: ComponentCustomPostRepeatableSection | null) => {
          if (!item) {
            return null;
          }
          const section = item as ComponentCustomPostRepeatableSection;
          const cta = (section.cta?.find(Boolean) || null) as ComponentCustomCta | null;
          const image = section?.image?.[0];
          const imageSrc = image?.url || '';
          const quote = section?.quote;

          return (
            <div
              key={item.id}
              className={clsx(classNameSection, {
                'content-paragraph mb-5 text-white/90 md:mb-6 lg:mb-8': !classNameSection,
              })}>
              {!!item.title && (
                <ContentHeading dark type="h2">
                  {item.title}
                </ContentHeading>
              )}
              {!!item.content?.length && <ContentRichText content={item.content} dark />}
              {!!imageSrc && image && (
                <Image
                  alt={image.alternativeText || ''}
                  className="h-auto w-full"
                  height={image.height as number}
                  src={imageSrc}
                  width={image.width as number}
                />
              )}
              {!!quote && renderQuote(quote, item.id)}
              {!!cta && (
                <CTA
                  dark
                  classNameHeight="h-auto"
                  fullHeight={false}
                  href={normalizeLocalizedHref(cta.href || '', i18n.language)}
                  text={cta.text || ''}
                  transparent
                  title={cta.title || ''}
                  subtitle={cta.subtitle || ''}
                />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};
